<template>
  <header
    class="site-header"
    :class="{ 'site-header--hidden': isScrolled }"
  >
    <nav class="site-nav">
      <div class="site-nav__inner container">
        <!-- Brand -->
        <div class="site-nav__brand" @click="scrollToSection('#hero')">
          <img
            :src="require('@/assets/dailyfit_logo.webp')"
            alt="dailyfit logo"
            class="site-nav__logo"
          />
          <div class="site-nav__brand-text">
            <span class="site-nav__brand-name">
              Daily<span class="site-nav__brand-accent">Fit</span>
              <span class="site-nav__brand-location"> - Oberviechtach</span>
            </span>
            <span class="site-nav__brand-tagline">Fitness & more</span>
          </div>
        </div>

        <!-- Desktop Nav Links -->
        <ul class="site-nav__links">
          <li
            v-for="(item, index) in menuItems"
            :key="index"
            class="site-nav__item"
          >
            <a
              :href="item.link"
              class="site-nav__link"
              :class="{ 'site-nav__link--active': currentSection === item.link.substring(1) }"
              @click="closeMobileMenu"
            >
              {{ item.text }}
            </a>
          </li>
        </ul>

        <!-- Mobile Toggle -->
        <button
          class="site-nav__toggle"
          :class="{ 'site-nav__toggle--open': isMobileMenuOpen }"
          @click="toggleMobileMenu"
          aria-label="Toggle navigation"
        >
          <span class="site-nav__toggle-bar"></span>
          <span class="site-nav__toggle-bar"></span>
          <span class="site-nav__toggle-bar"></span>
        </button>
      </div>
    </nav>

    <!-- Mobile Menu Overlay -->
    <transition name="mobile-menu">
      <div
        v-if="isMobileMenuOpen"
        class="mobile-menu"
      >
        <button class="mobile-menu__close" @click="closeMobileMenu" aria-label="Menü schließen">
          <span class="mobile-menu__close-bar"></span>
          <span class="mobile-menu__close-bar"></span>
        </button>
        <div class="mobile-menu__content">
          <ul class="mobile-menu__links">
            <li
              v-for="(item, index) in menuItems"
              :key="index"
              class="mobile-menu__item"
              :style="{ animationDelay: (index * 0.06 + 0.1) + 's' }"
            >
              <a
                :href="item.link"
                class="mobile-menu__link"
                :class="{ 'mobile-menu__link--active': currentSection === item.link.substring(1) }"
                @click="closeMobileMenu"
              >
                <span class="mobile-menu__link-text">{{ item.text }}</span>
                <fa-icon icon="chevron-right" class="mobile-menu__link-arrow" />
              </a>
            </li>
          </ul>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
export default {
  name: 'HeaderComp',
  data() {
    return {
      isMobileMenuOpen: false,
      isScrolled: false,
      lastScrollPosition: 0,
      scrollThreshold: 50,
      currentSection: 'hero',
      menuItems: [
        { text: 'Home', link: '#hero' },
        { text: 'Öffnungszeiten', link: '#opening-hours' },
        { text: 'Angebote', link: '#services' },
        { text: 'Über uns', link: '#about' },
        { text: 'Kontakt', link: '#contact' }
      ]
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('scroll', this.trackSection);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('scroll', this.trackSection);
  },
  methods: {
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    },
    closeMobileMenu() {
      this.isMobileMenuOpen = false;
    },
    handleScroll() {
      const currentScroll = window.scrollY;
      this.isScrolled = currentScroll > this.lastScrollPosition && currentScroll > this.scrollThreshold;
      this.lastScrollPosition = currentScroll;
    },
    scrollToSection(section) {
      this.currentSection = section.substring(1);
      document.querySelector(section)?.scrollIntoView({ behavior: 'smooth' });
      this.closeMobileMenu();
    },
    trackSection() {
      const sections = ['hero', 'services', 'opening-hours', 'about', 'contact'];
      const scrollPosition = window.scrollY;

      for (let section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const top = element.offsetTop;
          const height = element.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            this.currentSection = section;
            break;
          }
        }
      }
    }
  }
}
</script>

<style scoped>
/* === Header Shell === */
.site-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.site-header--hidden {
  transform: translateY(-100%);
}

/* === Nav Bar === */
.site-nav {
  margin: 12px 16px 0;
  background: rgba(10, 10, 10, 0.75);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.3),
    0 8px 32px rgba(0, 0, 0, 0.2);
}

.site-nav__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 24px;
}

/* === Brand === */
.site-nav__brand {
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  text-decoration: none;
}

.site-nav__logo {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  object-fit: cover;
  transition: opacity 0.2s ease;
}

.site-nav__brand:hover .site-nav__logo {
  opacity: 0.85;
}

.site-nav__brand-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.site-nav__brand-name {
  font-size: 1.125rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.site-nav__brand-accent {
  color: #7cff00;
  font-weight: 800;
}

.site-nav__brand-location {
  font-weight: 400;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.9375rem;
}

.site-nav__brand-tagline {
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.35);
  font-weight: 500;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

/* === Desktop Nav Links === */
.site-nav__links {
  display: flex;
  align-items: center;
  gap: 4px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.site-nav__link {
  position: relative;
  display: block;
  padding: 8px 16px;
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
  text-decoration: none;
  border-radius: 8px;
  transition: color 0.25s ease, background 0.25s ease;
  letter-spacing: 0.1px;
}

.site-nav__link::after {
  content: '';
  position: absolute;
  bottom: 4px;
  left: 16px;
  right: 16px;
  height: 1.5px;
  background: #7cff00;
  border-radius: 1px;
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.site-nav__link:hover {
  color: rgba(255, 255, 255, 0.9);
}

.site-nav__link:hover::after {
  transform: scaleX(1);
}

.site-nav__link--active {
  color: #7cff00;
}

.site-nav__link--active::after {
  transform: scaleX(1);
  background: #7cff00;
  opacity: 0.5;
}

/* === Mobile Toggle (Hamburger → X) === */
.site-nav__toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  gap: 5px;
  z-index: 1010;
}

.site-nav__toggle-bar {
  display: block;
  width: 22px;
  height: 2px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 2px;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  transform-origin: center;
}

.site-nav__toggle--open .site-nav__toggle-bar:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
  background: #7cff00;
}

.site-nav__toggle--open .site-nav__toggle-bar:nth-child(2) {
  opacity: 0;
  transform: scaleX(0);
}

.site-nav__toggle--open .site-nav__toggle-bar:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
  background: #7cff00;
}

/* === Mobile Menu Overlay === */
.mobile-menu {
  position: fixed;
  inset: 0;
  z-index: 999;
  background: rgba(8, 8, 8, 0.96);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Close Button */
.mobile-menu__close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  cursor: pointer;
  padding: 0;
  transition: all 0.25s ease;
  z-index: 1;
}

.mobile-menu__close:hover {
  background: rgba(124, 255, 0, 0.1);
  border-color: rgba(124, 255, 0, 0.2);
}

.mobile-menu__close-bar {
  position: absolute;
  width: 18px;
  height: 2px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 2px;
  transition: background 0.25s ease;
}

.mobile-menu__close-bar:nth-child(1) {
  transform: rotate(45deg);
}

.mobile-menu__close-bar:nth-child(2) {
  transform: rotate(-45deg);
}

.mobile-menu__close:hover .mobile-menu__close-bar {
  background: #7cff00;
}

.mobile-menu__content {
  width: 100%;
  max-width: 400px;
  padding: 0 24px;
}

.mobile-menu__links {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mobile-menu__item {
  opacity: 0;
  animation: menuItemReveal 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes menuItemReveal {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mobile-menu__link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  text-decoration: none;
  border-radius: 12px;
  transition: all 0.25s ease;
}

.mobile-menu__link-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: -0.3px;
  transition: color 0.25s ease;
}

.mobile-menu__link-arrow {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.1);
  transition: all 0.25s ease;
}

.mobile-menu__link:hover,
.mobile-menu__link:active {
  background: rgba(255, 255, 255, 0.04);
}

.mobile-menu__link:hover .mobile-menu__link-text {
  color: #ffffff;
}

.mobile-menu__link:hover .mobile-menu__link-arrow {
  color: #7cff00;
  transform: translateX(3px);
}

.mobile-menu__link--active {
  background: rgba(124, 255, 0, 0.06);
}

.mobile-menu__link--active .mobile-menu__link-text {
  color: #7cff00;
}

.mobile-menu__link--active .mobile-menu__link-arrow {
  color: rgba(124, 255, 0, 0.3);
}

/* === Transition for mobile menu === */
.mobile-menu-enter-active {
  transition: opacity 0.3s ease;
}

.mobile-menu-leave-active {
  transition: opacity 0.2s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
}

/* === Responsive === */
@media (max-width: 767.98px) {
  .site-nav {
    margin: 8px 12px 0;
    border-radius: 12px;
  }

  .site-nav__inner {
    padding: 8px 16px;
  }

  .site-nav__links {
    display: none;
  }

  .site-nav__toggle {
    display: flex;
  }

  .site-nav__brand-location {
    display: none;
  }

  .site-nav__brand-tagline {
    display: none;
  }

  .site-nav__logo {
    width: 36px;
    height: 36px;
    border-radius: 8px;
  }

  .site-nav__brand-name {
    font-size: 1.0625rem;
  }
}

@media (min-width: 768px) {
  .mobile-menu {
    display: none !important;
  }
}
</style>
